import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Sheet, SheetContent, SheetTitle } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation } from 'convex/react'
import { CalendarCheck, CalendarClock, CalendarX, RotateCw, SkipForward, X } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TodaySectionHeader } from '@/components/today-section-header'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { useMustDo } from '@/hooks/use-must-do'
import { type QueuedTask, type TodayTask, localDateStr } from '@/hooks/use-today-tasks'
import { useIsCoaching } from '@/lib/coaching-mode'
import { optimisticTaskStatus } from '@/lib/nodes-optimistic'
import { useMustDoStore } from '@/store/must-do.store'

// Days carried over before the lane asks what's in the way.
export const NUDGE_AFTER_DAYS = 3
const NUDGE_DISMISS_KEY = 'goaltree-mustdo-nudge-dismissed'

export function nudgeDismissed(taskId: string): boolean {
  try {
    return localStorage.getItem(NUDGE_DISMISS_KEY) === `${taskId}@${localDateStr()}`
  } catch {
    return false
  }
}

function dismissNudge(taskId: string) {
  try {
    localStorage.setItem(NUDGE_DISMISS_KEY, `${taskId}@${localDateStr()}`)
  } catch {}
}

export function MustDoHeader({ count, limit }: { count: number; limit: number }) {
  const { t } = useTranslation()
  return <TodaySectionHeader label={t('mustDo.title')} count={`${count}/${limit}`} />
}

// One row of the tinted lane card; position rounds the card's top/bottom edges.
export function MustDoRow({
  position,
  putOffDays,
  children,
}: {
  position: 'first' | 'mid' | 'last' | 'only'
  putOffDays: number
  children: ReactNode
}) {
  const { t } = useTranslation()
  const top = position === 'first' || position === 'only'
  const bottom = position === 'last' || position === 'only'
  return (
    <div
      className={cn(
        'border-x border-primary/30 bg-primary/6 px-2',
        top && 'rounded-t-2xl border-t pt-1',
        bottom && 'mb-2 rounded-b-2xl border-b pb-1',
      )}
    >
      {children}
      {putOffDays > 0 && (
        <p className="-mt-1 flex items-center gap-1 pb-2 pl-9 text-[10.5px] font-semibold text-amber-500">
          <RotateCw className="size-3" />
          {t('mustDo.putOff', { count: putOffDays })}
        </p>
      )}
    </div>
  )
}

export function MustDoNudge({
  task,
  onPickTime,
  onDismiss,
}: {
  task: TodayTask
  onPickTime: () => void
  onDismiss: () => void
}) {
  const { t, i18n } = useTranslation()
  const { isPro } = useIsPro()
  const setStatus = useMutation(api.nodes.setTaskStatus).withOptimisticUpdate(optimisticTaskStatus)
  const breakDown = useAction(api.weeklyReviewActions.breakDownTask)
  const mustDo = useMustDo()
  const coaching = useIsCoaching()
  const [busy, setBusy] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  const close = () => {
    dismissNudge(task.taskId as string)
    onDismiss()
  }

  async function handleBreakDown() {
    if (!isPro) {
      setUpgradeOpen(true)
      return
    }
    setBusy(true)
    try {
      const steps = await breakDown({ taskId: task.taskId, language: i18n.language })
      if (steps[0]) {
        await mustDo(task.taskId, null, true)
        await mustDo(steps[0].id as Id<'nodes'>, 'today', true)
      }
      toast.success(t('mustDo.nudge.splitDone', { count: steps.length }))
      close()
    } catch {
      toast.error(t('mustDo.error'))
    } finally {
      setBusy(false)
    }
  }

  const action = (label: string, onClick: () => void, primary = false) => (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={cn(
        'cursor-pointer rounded-xl border px-3 py-2 text-[12.5px] font-bold transition-colors disabled:opacity-60',
        primary
          ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
          : 'border-border bg-muted/40 hover:bg-muted',
      )}
    >
      {label}
    </button>
  )

  return (
    <div className="relative mb-3 rounded-2xl border border-border bg-card/70 p-3.5">
      <button
        type="button"
        onClick={close}
        aria-label={t('common.close')}
        className="absolute top-2.5 right-2.5 flex size-7 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
      >
        <X className="size-3.5" />
      </button>
      <p className="pr-8 text-[14px] font-bold text-balance">
        {t('mustDo.nudge.title', { title: task.title })}
      </p>
      <p className="mt-0.5 mb-3 text-[12.5px] text-muted-foreground">
        {t('mustDo.nudge.desc', { count: task.putOffDays })}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {action(
          t('mustDo.nudge.start'),
          () => {
            setStatus({ taskId: task.taskId, status: 'in-progress' }).catch(() => {})
            toast.success(t('mustDo.nudge.started'))
            close()
          },
          true,
        )}
        {action(t('mustDo.nudge.pickTime'), onPickTime)}
        {action(busy ? t('mustDo.nudge.splitting') : t('mustDo.nudge.breakDown'), handleBreakDown)}
        {!coaching &&
          action(t('mustDo.nudge.coach'), () =>
            window.dispatchEvent(
              new CustomEvent('open-coach', {
                detail: {
                  message: t('mustDo.nudge.coachPrompt', {
                    title: task.title,
                    count: task.putOffDays,
                  }),
                },
              }),
            ),
          )}
      </div>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="replan" />
    </div>
  )
}

export function QueuedTray({
  queued,
  onOpen,
}: {
  queued: QueuedTask[]
  onOpen?: (taskId: Id<'nodes'>) => void
}) {
  const { t } = useTranslation()
  const mustDo = useMustDo()
  return (
    <div className="mt-4 rounded-2xl border border-dashed border-border px-3.5 py-2.5">
      <TodaySectionHeader
        label={t('mustDo.queuedTitle')}
        count={String(queued.length)}
        className="mt-0"
      />
      {queued.map((q) => (
        <div key={q.taskId} className="group flex items-center gap-2.5 py-1.5">
          <span className="size-3.5 shrink-0 rounded-full border-[1.5px] border-muted-foreground/40" />
          <button
            type="button"
            onClick={() => onOpen?.(q.taskId)}
            className="min-w-0 flex-1 cursor-pointer truncate text-left text-[13px]"
          >
            {q.title}
          </button>
          <button
            type="button"
            onClick={() => mustDo(q.taskId, 'today')}
            className="shrink-0 cursor-pointer rounded-full px-2 py-0.5 text-[11px] font-bold text-primary-text opacity-70 transition-opacity hover:bg-primary/10 hover:opacity-100"
          >
            {t('mustDo.doToday')}
          </button>
        </div>
      ))}
      <p className="pt-0.5 text-[11px] text-muted-foreground/70">{t('mustDo.queuedHint')}</p>
    </div>
  )
}

// Shown when a commit hits the 3-task limit: pick one to defer, then the new one goes in.
export function MustDoFullDialog() {
  const { t } = useTranslation()
  const full = useMustDoStore((s) => s.full)
  const setFull = useMustDoStore((s) => s.setFull)
  const mustDo = useMustDo()
  const [busy, setBusy] = useState<string | null>(null)
  const [shown, setShown] = useState(full)
  if (full && full !== shown) setShown(full)
  const view = full ?? shown
  // Making room for today defers one to tomorrow; tomorrow's lane can only drop one.
  const forToday = view?.day === 'today'

  async function makeRoom(id: Id<'nodes'>) {
    if (!view) return
    setBusy(id as string)
    const moved = await mustDo(id, forToday ? 'tomorrow' : null, true)
    const added = moved && (await mustDo(view.taskId, view.day, true))
    setBusy(null)
    if (added) {
      toast.success(t(view.day === 'today' ? 'mustDo.addedToday' : 'mustDo.addedTomorrow'))
      setFull(null)
    }
  }

  return (
    <Dialog open={!!full} onOpenChange={(o) => !o && setFull(null)}>
      <DialogContent className="max-w-sm">
        <DialogTitle className="text-lg font-black">{t('mustDo.fullTitle')}</DialogTitle>
        <p className="-mt-1 text-sm text-muted-foreground">
          {t(forToday ? 'mustDo.fullAskToday' : 'mustDo.fullAskTomorrow')}
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {view?.lane.map((task) => (
            <div
              key={task.taskId}
              className="flex items-center gap-3 rounded-xl border border-border bg-card/60 px-3 py-2.5"
            >
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
                {task.title}
              </span>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => makeRoom(task.taskId)}
                className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs font-bold transition-colors hover:bg-muted/70 disabled:opacity-60"
              >
                {busy === task.taskId ? (
                  <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : forToday ? (
                  <CalendarClock className="size-3.5" />
                ) : (
                  <CalendarX className="size-3.5" />
                )}
                {t(forToday ? 'mustDo.moveTomorrow' : 'mustDo.removeShort')}
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setFull(null)}
          className="mt-1 cursor-pointer py-1 text-center text-[13px] text-muted-foreground hover:text-foreground"
        >
          {t('common.cancel')}
        </button>
      </DialogContent>
    </Dialog>
  )
}

// Today's swipe-left: choose between skipping and the Must do lane instead of
// skipping straight away.
export function SwipeActionSheet({
  open,
  onOpenChange,
  title,
  committed,
  onCommit,
  onSkip,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  committed: boolean
  onCommit: (day: 'today' | 'tomorrow' | null) => void
  onSkip?: () => void
}) {
  const { t } = useTranslation()
  const pick = (fn: () => void) => () => {
    onOpenChange(false)
    fn()
  }
  const row = (icon: ReactNode, label: string, onClick: () => void, tone?: string) => (
    <button
      type="button"
      onClick={pick(onClick)}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-3 text-left text-[15px] font-semibold transition-colors hover:bg-muted [&_svg]:size-5',
        tone,
      )}
    >
      {icon}
      {label}
    </button>
  )
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="rounded-t-2xl px-3 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)] sm:bottom-4 sm:mx-auto sm:max-w-[420px] sm:rounded-2xl sm:border"
      >
        <SheetTitle className="truncate px-3 pb-1 text-[13px] font-semibold text-muted-foreground">
          {title}
        </SheetTitle>
        {committed
          ? row(<CalendarX />, t('mustDo.remove'), () => onCommit(null))
          : row(
              <CalendarCheck />,
              t('mustDo.doToday'),
              () => onCommit('today'),
              'text-primary-text',
            )}
        {row(<CalendarClock />, t('mustDo.doTomorrow'), () => onCommit('tomorrow'))}
        {onSkip && !committed && row(<SkipForward />, t('today.skipToday'), onSkip)}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="mt-1 w-full cursor-pointer rounded-xl bg-muted/60 py-3 text-[15px] font-semibold transition-colors hover:bg-muted"
        >
          {t('common.cancel')}
        </button>
      </SheetContent>
    </Sheet>
  )
}
